import firebase from 'firebase/app';
import 'firebase/auth';

import { fplsFetch, getActiveReactPage } from '../../../../r/shared/utils';
import { resetAmplitudeSession } from '../../../../r/shared/amplitude';
import { updateFeedback } from '../reducers';
import win from '../../../../r/shared/win';

const { AUTH_RECAPTCHA_SITE_KEY_V3, GOOGLE_AUTH_CLIENT_ID, FIREBASE_CONFIG } = win;

export const coreDependenciesAreLoaded = () => (isGoogleSDKLoaded() && isRecaptchaSDKLoaded());

export const firebaseAuth = (credential) => (dispatch, getState) => {
  const { password, email } = getState().auth.model;

  firebase.auth().signInWithCredential(credential)
  .catch((error) => {
    const errorCode = error.code;
    
    dispatch(updateFeedback({
      isLoading: false,
      action: ''
    }));
    
    console.log(error);

    if (errorCode === 'auth/account-exists-with-different-credential') {
      return dispatch(updateFeedback({
        message: 'Email already associated with another provider',
        success: false
      }));
    }

    if (errorCode === 'auth/wrong-password') {
      return dispatch(updateFeedback({
        message: 'Invalid Email Address or Password',
        success: false
      }));
    }

    if (errorCode === 'auth/user-not-found') {
      dispatch(updateFeedback({
        action: 'Creating account...',
        isLoading: true,
        message: ''
      }));

      firebase.auth().createUserWithEmailAndPassword(email, password)
      .catch((error) => {
        console.error('Sign-in error', error.message, error);
        dispatch(updateFeedback({
          message: error.message,
          success: false
        }));
      }).finally(() => {
        dispatch(updateFeedback({
          isLoading: false,
          action: ''
        }));
      });
      
      return;
    }
    
    console.error('Sign-in error', error.message, error);
    dispatch(updateFeedback({
      message: error.message,
      success: false
    }));
  });
};

export const validateUserToken = (user) => async (dispatch) => {
  dispatch(updateFeedback({
    action: 'Processing...',
    isLoading: true,
    message: ''
  }));

  const email = user.email;
  
  console.log('Extracted email: ', email);

  const response = await checkSpamEmail(email);

  if (response?.is_spammed) {
    return dispatch(updateFeedback({
      message: 'Suspicious activity detected. Signup not allowed!',
      isLoading: false,
      success: false,
      action: ''
    }));
  }

  user.getIdToken().then((idToken) => {
    const returnUrl = sessionStorage.getItem('return_url');
    const url = `/login/${idToken}?redirect=${returnUrl}`;

    fplsFetch({
      method: 'POST',
      url,
    }).then((response) => {
      if (response.error) {
        dispatch(updateFeedback({
          message: response.error || 'Oh dear! Something went wrong. Please refresh page and try again',
          isLoading: false,
          success: false,
          action: ''
        }));
        
        return firebase.auth().signOut();
      }

      const requiresDetailsUpdate = response.redirect.includes('/r/details');
      const requires2FA = response.redirect.includes('confirm-2fa');
      let intendedRoute = returnUrl || response.redirect || '/r';
      
      if (requiresDetailsUpdate || requires2FA) {
        intendedRoute = response.redirect;
      } else {
        sessionStorage.removeItem('return_url');
      }

      console.log('Firebase logged in...');
      window.location.href = intendedRoute;
    });
  });
};

export const resetPassword = () => (dispatch, getState) => {
  dispatch(updateFeedback({
    action: 'Sending email...',
    isLoading: true
  }));

  const { email } = getState().auth.model;
  testingCaptcha('resetpassword');
  
  firebase.auth().sendPasswordResetEmail(email)
  .then(() => {
    dispatch(updateFeedback({
      message: 'Reset password email sent.',
      success: true
    }));

    const timeout = setTimeout(() => {
      window.location.href = `/e/auth`;
      clearTimeout(timeout);
    }, 1000);
  }).catch((error) => {
    dispatch(updateFeedback({
      message: error.message,
      success: false
    }));

    console.log(error);
  }).finally(() => {
    dispatch(updateFeedback({
      isLoading: false,
      action: ''
    }));
  });
};

export const signUp = () => async (dispatch, getState) => {
  const { password, email } = getState().auth.model;
  const { EmailAuthProvider } = firebase.auth;

  dispatch(updateFeedback({
    action: 'Processing...',
    isLoading: true,
    message: ''
  }));
  
  testingCaptcha('signup');

  const response = await checkSpamEmail(email);
  
  if (response.is_spammed) {
    return dispatch(updateFeedback({
      message: 'Suspicious activity detected. Signup not allowed!',
      isLoading: false,
      success: false,
      action: ''
    }));
  }

  const credential = EmailAuthProvider.credential(email, password);
  dispatch(firebaseAuth(credential));
};

export const signIn = () => async (dispatch, getState) => {
  const { password, email } = getState().auth.model;
  const { EmailAuthProvider } = firebase.auth

  dispatch(updateFeedback({
    action: 'Processing...',
    isLoading: true,
    message: ''
  }));

  testingCaptcha('signin');

  const response = await checkSpamEmail(email);
  
  if (response.is_spammed) {
    return dispatch(updateFeedback({
      message: 'Suspicious activity detected. Signup not allowed!',
      isLoading: false,
      success: false,
      action: ''
    }));
  }

  const credential = EmailAuthProvider.credential(email, password);
  dispatch(firebaseAuth(credential));
};

export const confirm2FA = () => (dispatch, getState) => {
  const searchParams = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  if (!searchParams.id) return dispatch(updateFeedback({
    message: 'Invalid operation!',
    isLoading: false,
    success: false,
    action: ''
  }));
  
  const { otp } = getState().auth.model;

  dispatch(updateFeedback({
    action: 'Verifying...',
    isLoading: true
  }));

  testingCaptcha('confirm2fa');

  fplsFetch({
    url: get2FAEndpoint(),
    removeToken: true,
    method: 'POST',
    data: {
      id: searchParams.id,
      code: otp
    }
  }).then((response) => {
    if (response.error) return dispatch(updateFeedback({
      message: response.error,
      success: false
    }));
    
    const returnUrl = sessionStorage.getItem('return_url');
    const intendedRoute = returnUrl || response.redirect || '/r';
    sessionStorage.removeItem('return_url');
    console.log('Firebase logged in...');
    window.location.href = intendedRoute;
  }).finally(() => {
    dispatch(updateFeedback({
      isLoading: false,
      action: ''
    }));
  });
};

export const signInWithGoogle = () => (dispatch) => {
  const { GoogleAuthProvider } = firebase.auth;
  
  const client = google.accounts.oauth2.initTokenClient({
    client_id: GOOGLE_AUTH_CLIENT_ID,
    scope: 'openid email profile',
    callback: (response) => {
      if (response.error) {
        return dispatch(updateFeedback({
          message: 'Oh dear! Something went wrong. Please refresh page and try again',
          isLoading: false,
          success: false,
          action: ''
        }));
      }
      
      const accessToken = response.access_token ?? null;
      const idToken = response.credential ?? null;
      const hasToken = !!(accessToken || idToken);

      if (!hasToken) {
        return dispatch(updateFeedback({
          message: 'Oops! Unable to authorize user. Please refresh the page and try again',
          isLoading: false,
          success: false,
          action: ''
        }));
      }

      const credential = GoogleAuthProvider.credential(idToken, accessToken);
      dispatch(firebaseAuth(credential));
    },
  });

  client.requestAccessToken();
};

export const invalidateSession = (email = '') => {
  fplsFetch({
    url: '/invalidate_session',
    method: 'DELETE',
    data: { email }
  }).finally(() => {
    const page = getActiveReactPage();
    if (page.name === 'SignIn') return;

    // Save last visited page for re-routing on re-authentication.
    sessionStorage.setItem('return_url', window.location.href);
    window.location.href = `/e/auth`;
    resetAmplitudeSession();
  });
};

export const checkSpamEmail = async (email) => {
  try {
    const response = await fplsFetch({
      url: `/api/v2/public/check-spam-email?email=${email}`,
      type: 'GET'
    });
    
    return response;
  } catch (error) {
    console.error('Error checking spam email: ', error);
    throw error;
  }
};

export const testingCaptcha = (action) => {
  grecaptcha.ready(() => {
    grecaptcha.execute(AUTH_RECAPTCHA_SITE_KEY_V3, {
      action
    });
  });
};

export const isRecaptchaSDKLoaded = () => (
  typeof window !== 'undefined' &&
  typeof window.grecaptcha === 'object'
);

export const isGoogleSDKLoaded = () => (
  typeof window !== 'undefined' &&
  typeof window.google === 'object'
);

export const loadReCaptchaSDK = () => {
  const script = document.createElement('script');
  script.src = `https://www.google.com/recaptcha/api.js?render=${AUTH_RECAPTCHA_SITE_KEY_V3}`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

export const get2FAEndpoint = () => {
  // Only useful on confirm-2fa route.
  const url = new URL(window.location.href);
  return url.pathname.replace(/^\/e/, '');
};

export const loadGoogleSDK = () => {
  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
};

const initializeApp = () => {
  if (firebase.apps.length) return;
  firebase.initializeApp(FIREBASE_CONFIG);
};

initializeApp();